// SportModel.ts 

import { FrontEndModel } from '../FrontEndModel';
import { SportName, BodyPart, BaseSoccerPositionValues } from "@/../types/enums";
import { isEmpty, isNotEmpty } from "@/pipes";
import { cloneDeep } from 'lodash';
import { AthleteReportCategoryHeading } from '@/models';

const AthleteReportDefaultHeadings = [
	{
		name:'Technical', rating: undefined, notes: undefined, expanded: false,
		categories: [
			{name:'Dribbling', rating: undefined, notes: undefined},
			{name:'Passing', rating: undefined, notes: undefined},
			{name:'Shooting', rating: undefined, notes: undefined},
			{name:'Control', rating: undefined, notes: undefined},
		]
	},
	{
		name:'Tactical', rating: undefined, notes: undefined, expanded: false,
		categories: [
			{name:'Positioning', rating: undefined, notes: undefined},
			{name:'Movement', rating: undefined, notes: undefined},
			{name:'Awareness', rating: undefined, notes: undefined},
			{name:'Decisions', rating: undefined, notes: undefined},
		]
	},
	{
		name:'Physical', rating: undefined, notes: undefined, expanded: false,
		categories: [
			{name:'Speed', rating: undefined, notes: undefined},
			{name:'Strength', rating: undefined, notes: undefined},
			{name:'Stamina', rating: undefined, notes: undefined},
			{name:'Agility', rating: undefined, notes: undefined}
		]
	},
	{
		name:'Mental', rating: undefined, notes: undefined, expanded: false,
		categories: [
			{name:'Coachability', rating: undefined, notes: undefined},
			{name:'Confidence', rating: undefined, notes: undefined},
			{name:'Focus', rating: undefined, notes: undefined},
			{name:'Leadership', rating: undefined, notes: undefined},
		]
	},
	{
		name:'Social', rating: undefined, notes: undefined, expanded: false,
		categories: [
			{name:'Teamwork', rating: undefined, notes: undefined},
			{name:'Respect', rating: undefined, notes: undefined},
			{name:'Sportsmanship', rating: undefined, notes: undefined},
			{name:'Adaptability', rating: undefined, notes: undefined},
		]
	},
];	

export class SportLabel {
	label: string;
	icon?: string;
	abbreviation?: string;
	description?: string;
	measurement?: string;
	units?: string;
	unitsDerived?: string;

	load(obj: Record<string, any>): this {
		Object.assign(this, obj);

		if( isEmpty(this.abbreviation) && isNotEmpty(this.label) ) this.abbreviation = (this.label.length >= 3)? this.label.substring(0,2) : this.label;
		if( isEmpty(this.description) ) this.description = this.label;
		return this;
	}
}
export class SportModel extends FrontEndModel {
    name!: SportName;
    icon: string = '';
    active: boolean = true;
    team: boolean = true;
    positions?: string[];
    bodyParts?: BodyPart[];
    physical: boolean = false;
    physicalLabels?: string[];											// deprecated. replaced with physicalAssessmentLabels
	physicalAssessmentLabels?: Array<SportLabel>;
    athleteReports: boolean = true;
    athleteReportHeadings: Array<AthleteReportCategoryHeading> = [];
    scouting: boolean = false;
    scoutingLabels?: string[];
    scoutingMoments?: string[];
    scoutingEvaluations?: string[];

    proSearch: boolean = false;
    supportDepthCharts: boolean = false;

    constructor() {
        super();

        this.name = SportName.Soccer;
        this.icon = "mdi-soccer";
        this.active = true;
        this.team = true;
        this.positions = BaseSoccerPositionValues;
        this.bodyParts = [BodyPart.Foot];
        this.physical = true;
        this.physicalLabels = undefined;
		this.physicalAssessmentLabels = [
			new SportLabel().load({ label: 'Acceleration', abbreviation: 'Acl', description: "Sprint time over the first 10m", measurement: "Running time from 0m to 10m", units: 's', unitsDerived: 'm/s^2' }),
			new SportLabel().load({ label: 'Speed', abbreviation: 'Spd', description: "Maximal running speed during a sprint", measurement: "Running time from 20m and 35m", units: 's', unitsDerived: 'm/s' }),
			new SportLabel().load({ label: 'Agility', abbreviation: 'Agl', description: "Reactive strength index using a Drop Jump", measurement: "Drop Jump Test. Jump height and ground contact time", units: 'cm', unitsDerived: '' }),
			new SportLabel().load({ label: 'Power', abbreviation: 'Pow', description: "Leg muscle power using Counter Movement Jump", measurement: "Counter Movement Jump. Jump height", units: 'cm', unitsDerived: 'W' }),
			new SportLabel().load({ label: 'Recovery', abbreviation: 'Rec', description: "Distance covered during a YoYo Test", measurement: "YoYo Test", units: 'stage', unitsDerived: 'km/h' }),
		]
        this.athleteReports = true;
        this.athleteReportHeadings = [...AthleteReportDefaultHeadings];
        this.scouting = true;
        this.scoutingLabels = ['Technical','Tactical','Physical','Mental'];
        this.scoutingMoments = ['Attacking Organization','Attacking Transition','Attacking Set Plays','Defending Organization','Defending Transition','Defending Set Plays'];
        this.scoutingEvaluations = ['Competitive', 'Resilience', 'Intelligence', 'Speed', 'Presence'];
        this.proSearch = false;
        this.supportDepthCharts = false;
    }

	load(obj: Record<string, any>): this {
		Object.assign(this, obj);

		if( isEmpty(obj.physicalAssessmentLabels) ) {
			// convert deprecated physicalLabels with physicalAssessmentLabels
			if( isNotEmpty(obj.physicalLabels) ) {
				this.physicalAssessmentLabels = obj.physicalLabels.map(l => { 
					const assessmentLabel: SportLabel = new SportLabel().load({label: l})
					return assessmentLabel;
				});
			}
		}
		if( isNotEmpty(obj['athleteReportHeadings']) ) {
			this.athleteReportHeadings = cloneDeep(obj['athleteReportHeadings']);
		} else {
			this.athleteReportHeadings = cloneDeep(AthleteReportDefaultHeadings);
		}

        return this;
    }

    get Positions(): string[] | null {
        if( !this.positions ) return null;
        if( this.positions.length == 0 ) return null;

        return this.positions;
    }

	get IsSoccer(): boolean {
		return this.name === SportName.Soccer;
	}
}

export type PartialSport = Partial<SportModel> & { id: string };